import { formatCurrency } from "@scandotcom/react";
import { MapPopup } from "@scandotcom/react/dist/esm/components/Map";
import { IconMapPin } from "@tabler/icons-react";
import React from "react";
import { IconRapid } from "../Icons";
import { LocationResult } from "../NewSearchResults/types";
import { Chip } from "../shared/Chip";
import { StarRating } from "../shared/StarRating";
import { isNil } from "../utils";

interface Props {
  location: LocationResult;
  onClose: () => void;
  modalitySlug?: string;
}

export function Popup({ location, onClose, modalitySlug }: Props) {
  return (
    <MapPopup
      showBeak={false}
      className="z-[3] min-w-[400px] font-normal"
      onClose={onClose}
      longitude={location.longitude}
      latitude={location.latitude}
    >
      <article className="flex flex-col gap-4">
        {location.image_src ? (
          <img
            loading="lazy"
            src={location.image_src}
            alt={location.name}
            className="h-48 rounded-2xl object-cover object-center"
          />
        ) : (
          <div className="h-48 rounded-2xl bg-neutral-50"></div>
        )}
        <div>
          <h3 className="font-maison-extended text-base font-bold text-neutral-900">
            <a href={`/locations/${location.slug}`} className="hover:underline">
              {location.name}
            </a>
          </h3>

          {Boolean(location.reviews && location.rating) && (
            <div data-testid="reviews" className="mb-2">
              <a
                href={`/locations/${location.slug}/#reviews`}
                className="flex items-center gap-x-1 text-sm font-semibold"
              >
                <StarRating rating={location.rating} className="h-4" />{" "}
                <span>{location.rating}</span>
                <span className="text-neutral-500 underline hover:no-underline">
                  ({location.reviews}{" "}
                  {location.reviews === 1 ? "review" : "reviews"})
                </span>
              </a>
            </div>
          )}

          <div
            data-testid="address"
            className="my-2 space-x-1 text-sm text-neutral-600"
          >
            <IconMapPin height={20} className="inline" />

            <address className="inline font-normal not-italic ">
              {location.address}, {location.town}, {location.postcode}
            </address>
          </div>

          <div data-testid="tags" className="flex flex-wrap gap-1.5">
            {location.has_next_day_slots && (
              <Chip variant="primary">
                <IconRapid className="mr-1.5 h-5 w-5" />
                Next day appointments
              </Chip>
            )}

            {Boolean(location.open_scanner) && (
              <Chip variant="secondary">Open scanner</Chip>
            )}

            {!isNil(location.min_age) && location.min_age > 0 && (
              <Chip variant="secondary">Min age: {location.min_age}+</Chip>
            )}
          </div>
        </div>

        <div className="flex flex-wrap justify-between">
          <div>
            <div className="font-maison-extended text-lg font-bold text-neutral-900">
              {formatCurrency(location.price / 100)}
            </div>
            <span className="font-medium text-neutral-500">Starting from</span>
          </div>

          <a
            href={`/locations/${location.slug}?modality=${modalitySlug}`}
            className="button button-primary mt-auto h-fit"
          >
            Book now
          </a>
        </div>
      </article>
    </MapPopup>
  );
}
